import React, { useEffect, useState } from "react";
import Modal from "../shared/Modal";
import { TextInput } from "../atoms";
import useAuth from "../../Hooks/useAuth";
import { useAlert } from "../../context/AlertContext";
import { useSendEmailChangeCodeMutation, useConfirmEmailChangeMutation } from "../../store/authApi";
import { getEmailSendErrorMessage } from "../../store/utils";

// Profil e-posta değiştirme (contract panel-profile 1.0): 1) yeni e-posta → kod
// yeni adrese gider, 2) kod → e-posta değişir, diğer oturumlar düşer; bu oturum
// yanıttaki yeni token'la sürer.

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const errText = (e, fallback) => e?.data?.message || fallback;

function EmailChangeModal({ open, onClose, currentEmail }) {
  const { auth, setAuth } = useAuth();
  const { showSuccess, showError } = useAlert();
  const [sendCode, { isLoading: sending }] = useSendEmailChangeCodeMutation();
  const [confirmChange, { isLoading: confirming }] = useConfirmEmailChangeMutation();
  const [step, setStep] = useState(1);
  const [newEmail, setNewEmail] = useState("");
  const [code, setCode] = useState("");
  const [sentTo, setSentTo] = useState("");

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setNewEmail("");
    setCode("");
    setSentTo("");
  }, [open]);

  const email = newEmail.trim().toLowerCase();
  const emailError =
    email && !EMAIL_RE.test(email)
      ? "Geçerli bir e-posta adresi girin."
      : email && email === String(currentEmail || "").toLowerCase()
        ? "Yeni e-posta mevcut adresinizle aynı."
        : "";

  const requestCode = async () => {
    if (!email || emailError || sending) return;
    try {
      const res = await sendCode({ newEmail: email, token: auth?.accessToken }).unwrap();
      setSentTo(res?.sentTo || email);
      setCode("");
      setStep(2);
    } catch (e) {
      const status = e?.status ?? e?.data?.statusCode;
      showError(status === 503 ? getEmailSendErrorMessage(e) : errText(e, "Kod gönderilemedi."));
    }
  };

  const confirm = async () => {
    if (code.length !== 6 || confirming) return;
    try {
      const res = await confirmChange({ code, token: auth?.accessToken }).unwrap();
      const user = res?.data?.user;
      if (user) setAuth({ ...user, accessToken: res?.token || auth?.accessToken });
      showSuccess("E-posta adresiniz güncellendi.");
      onClose();
    } catch (e) {
      showError(errText(e, "E-posta değiştirilemedi."));
      // Süresi dolan / deneme hakkı biten kod sunucuda silinir — yeniden kod istenmeli.
      if (e?.status === 429 || /süresi doldu|kod isteyin/i.test(e?.data?.message || "")) setStep(1);
    }
  };

  const primaryBtn =
    "px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-Medium hover:bg-indigo-700 disabled:opacity-50";
  const secondaryBtn = "px-4 py-2 rounded-lg text-sm font-Medium text-gray-600 hover:bg-gray-100 disabled:opacity-50";

  return (
    <Modal open={open} onClose={sending || confirming ? undefined : onClose} maxWidth="md">
      <Modal.Header
        title="E-posta adresini değiştir"
        subtitle={step === 1 ? `Mevcut: ${currentEmail || "—"}` : undefined}
        onClose={sending || confirming ? undefined : onClose}
      />
      <Modal.Body className="space-y-4">
        {step === 1 ? (
          <>
            <TextInput
              label="Yeni e-posta"
              type="email"
              autoFocus
              value={newEmail}
              placeholder="ornek@firma.com"
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && requestCode()}
              error={Boolean(emailError)}
              maxLength={100}
            />
            {emailError && <p className="text-xs text-red-600 -mt-2">{emailError}</p>}
            <p className="text-xs text-gray-500">
              Yeni adrese 6 haneli bir doğrulama kodu gönderilecek. Kodu girdiğinizde e-postanız değişir ve
              diğer cihazlardaki oturumlarınız kapanır.
            </p>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-600">
              Kod <strong>{sentTo}</strong> adresine gönderildi. 10 dakika geçerlidir.
            </p>
            <TextInput
              label="Doğrulama kodu"
              inputMode="numeric"
              autoFocus
              value={code}
              placeholder="6 haneli kod"
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              onKeyDown={(e) => e.key === "Enter" && confirm()}
              aria-label="Doğrulama kodu"
            />
            <div className="flex items-center justify-between text-xs">
              <button type="button" onClick={() => setStep(1)} className="text-gray-500 hover:text-gray-700">
                Adresi değiştir
              </button>
              <button
                type="button"
                onClick={requestCode}
                disabled={sending}
                className="text-indigo-600 hover:text-indigo-800 font-Medium disabled:opacity-50"
              >
                {sending ? "Gönderiliyor..." : "Kodu yeniden gönder"}
              </button>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="flex justify-end gap-2">
        <button type="button" onClick={onClose} disabled={sending || confirming} className={secondaryBtn}>
          Vazgeç
        </button>
        {step === 1 ? (
          <button type="button" onClick={requestCode} disabled={!email || Boolean(emailError) || sending} className={primaryBtn}>
            {sending ? "Gönderiliyor..." : "Kod gönder"}
          </button>
        ) : (
          <button type="button" onClick={confirm} disabled={code.length !== 6 || confirming} className={primaryBtn}>
            {confirming ? "Doğrulanıyor..." : "Onayla ve değiştir"}
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default EmailChangeModal;
